import { PRESET_SURVEYOR_CODES, isValidSurveyorCode, normalizeSurveyorCode } from './surveyQrUrls';
import { getSurveySurveyors, saveSurveySurveyor, approveSurveySurveyor, rejectSurveySurveyor, patchSurveySurveyorStatus } from '../supabaseService';

const STORAGE_KEY = 'de_survey_surveyor_registry';

function readRaw() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_err) {
    return {};
  }
}

function writeRaw(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

/** @returns {Record<string, { name: string, status?: string, phone?: string, village?: string, updatedAt: string }>} */
export function getSurveyorRegistry() {
  return readRaw();
}

export function getSurveyorName(code) {
  const key = normalizeSurveyorCode(code);
  if (!key) return '';
  return String(readRaw()[key]?.name || '').trim();
}

export function setSurveyorName(code, name) {
  const key = normalizeSurveyorCode(code);
  if (!isValidSurveyorCode(key)) return false;
  const data = readRaw();
  const trimmed = String(name || '').trim();
  if (!trimmed) {
    delete data[key];
  } else {
    data[key] = { name: trimmed, updatedAt: new Date().toISOString() };
  }
  writeRaw(data);
  return true;
}

/** Load freelancer names from server into localStorage cache. */
export async function syncSurveyorsFromServer() {
  const { items, error } = await getSurveySurveyors();
  if (error) return { ok: false, error };
  const data = readRaw();
  const serverCodes = new Set();
  (items || []).forEach((row) => {
    const code = normalizeSurveyorCode(row.code);
    const name = String(row.name || '').trim();
    if (!isValidSurveyorCode(code) || !name) return;
    serverCodes.add(code);
    const existing = data[code];
    const serverAt = row.updated_at ? new Date(row.updated_at).getTime() : 0;
    const localAt = existing?.updatedAt ? new Date(existing.updatedAt).getTime() : 0;
    if (!existing || serverAt >= localAt) {
      data[code] = {
        id: row.id || existing?.id || null,
        name,
        status: row.status || 'active',
        phone: row.phone || '',
        village: row.village || '',
        upiId: row.upi_id || '',
        bankAccount: row.bank_account || '',
        bankIfsc: row.bank_ifsc || '',
        updatedAt: row.updated_at || new Date().toISOString(),
      };
    }
  });
  Object.keys(data).forEach((code) => {
    if (!PRESET_SURVEYOR_CODES.includes(code) && !serverCodes.has(code)) {
      delete data[code];
    }
  });
  writeRaw(data);
  return { ok: true, error: '' };
}

/** Save locally and persist to server (admin API). Name required — use deleteSurveyorAsync to remove. */
export async function setSurveyorNameAsync(code, name, paymentDetails = {}) {
  const key = normalizeSurveyorCode(code);
  if (!isValidSurveyorCode(key)) return { ok: false, error: 'Invalid code' };
  const trimmed = String(name || '').trim();
  if (!trimmed) return { ok: false, error: 'Name required' };
  setSurveyorName(key, trimmed);
  const { error } = await saveSurveySurveyor(key, trimmed, paymentDetails);
  if (error) return { ok: false, error };
  const data = readRaw();
  if (data[key]) {
    data[key].status = 'active';
    if (paymentDetails.upi_id !== undefined) data[key].upiId = paymentDetails.upi_id || '';
    if (paymentDetails.bank_account !== undefined) data[key].bankAccount = paymentDetails.bank_account || '';
    if (paymentDetails.bank_ifsc !== undefined) data[key].bankIfsc = paymentDetails.bank_ifsc || '';
  }
  writeRaw(data);
  return { ok: true, error: '' };
}

export async function approveSurveyorAsync(code) {
  const key = normalizeSurveyorCode(code);
  if (!isValidSurveyorCode(key)) return { ok: false, error: 'Invalid code' };
  const { error } = await approveSurveySurveyor(key);
  if (error) return { ok: false, error };
  const data = readRaw();
  if (data[key]) {
    data[key].status = 'active';
    data[key].updatedAt = new Date().toISOString();
  }
  writeRaw(data);
  return { ok: true, error: '' };
}

export async function rejectSurveyorAsync(code) {
  const key = normalizeSurveyorCode(code);
  if (!isValidSurveyorCode(key)) return { ok: false, error: 'Invalid code' };
  if (PRESET_SURVEYOR_CODES.includes(key)) {
    return { ok: false, error: 'Preset codes cannot be rejected' };
  }
  const data = readRaw();
  delete data[key];
  writeRaw(data);
  const { error } = await rejectSurveySurveyor(key);
  if (error) return { ok: false, error };
  return { ok: true, error: '' };
}

export async function setFreelancerStatusAsync(code, status) {
  const key = normalizeSurveyorCode(code);
  if (!isValidSurveyorCode(key)) return { ok: false, error: 'Invalid code' };
  if (!['active', 'inactive'].includes(status)) return { ok: false, error: 'Invalid status' };
  const { error } = await patchSurveySurveyorStatus(key, status);
  if (error) return { ok: false, error };
  const data = readRaw();
  if (data[key]) {
    data[key].status = status;
    data[key].updatedAt = new Date().toISOString();
  }
  writeRaw(data);
  return { ok: true, error: '' };
}

export function isFreelancerBlocked(code) {
  const key = normalizeSurveyorCode(code);
  const status = readRaw()[key]?.status;
  return status === 'inactive' || status === 'rejected';
}

export function countPendingSurveyors() {
  return listSurveyors().filter((f) => f.status === 'pending').length;
}

/** Remove freelancer from registry and server (inactive / no leads). */
export async function deleteSurveyorAsync(code) {
  const key = normalizeSurveyorCode(code);
  if (!isValidSurveyorCode(key)) return { ok: false, error: 'Invalid code' };
  if (PRESET_SURVEYOR_CODES.includes(key)) {
    return { ok: false, error: 'Preset codes cannot be deleted' };
  }
  const data = readRaw();
  delete data[key];
  writeRaw(data);
  const { error } = await saveSurveySurveyor(key, '');
  if (error) return { ok: false, error };
  return { ok: true, error: '' };
}

export function canDeleteSurveyor(code) {
  const key = normalizeSurveyorCode(code);
  const entry = readRaw()[key];
  return isValidSurveyorCode(key)
    && !PRESET_SURVEYOR_CODES.includes(key)
    && Boolean(entry?.name)
    && (entry?.status || 'active') === 'active';
}

/** Sorted list: presets first, then by code. */
export function listSurveyors() {
  const data = readRaw();
  const codes = new Set([...PRESET_SURVEYOR_CODES, ...Object.keys(data)]);
  return [...codes]
    .filter((c) => isValidSurveyorCode(c))
    .sort((a, b) => {
      const aPreset = PRESET_SURVEYOR_CODES.indexOf(a);
      const bPreset = PRESET_SURVEYOR_CODES.indexOf(b);
      if (aPreset >= 0 && bPreset >= 0) return aPreset - bPreset;
      if (aPreset >= 0) return -1;
      if (bPreset >= 0) return 1;
      return a.localeCompare(b);
    })
    .map((code) => ({
      code,
      name: data[code]?.name || '',
      status: data[code]?.status || (data[code]?.name ? 'active' : ''),
      phone: data[code]?.phone || '',
      village: data[code]?.village || '',
      updatedAt: data[code]?.updatedAt || null,
    }));
}

export function formatSurveyorLabel(code, registry = null) {
  const key = normalizeSurveyorCode(code);
  if (!key) return '—';
  const reg = registry || readRaw();
  const name = String(reg[key]?.name || '').trim();
  if (name) return `${name} (${key})`;
  return key;
}

/** Suggest next unused code like FR01, FR02, … */
export function suggestNextSurveyorCode(registry = null) {
  const reg = registry || readRaw();
  const used = new Set([...Object.keys(reg), ...PRESET_SURVEYOR_CODES]);
  for (let n = 1; n <= 99; n += 1) {
    const code = `FR${String(n).padStart(2, '0')}`;
    if (!used.has(code)) return code;
  }
  return `FR${Date.now().toString(36).toUpperCase().slice(-4)}`;
}
